import React, { ReactElement, useEffect, useState } from 'react';
import { Alert, Col, Row } from 'react-bootstrap';
import { useQuery } from 'react-query';
import * as yup from 'yup';

import BaseModal from '../../../components/BaseModal/BaseModal';
import Forms from '../../../components/Forms/Forms';
import Loading from '../../../components/Loading/Loading';
import WSPRequestView from '../../../components/modals/RequestCIBAgreementModal/views/CIBRequestView';
import {
	getDeclaredPriceAgreement,
	getDeclaredPriceRequestAgreementTemplates,
	postDeclaredPriceRequests,
} from '../../../services/digitalCodesService';
import { isEmptyObject } from '../../../utils/dataUtils';
import { getSanitizedHTML } from '../../../utils/stringUtils';
import { createMessageForError, toasterNotify } from '../../../utils/toaster';
import { validateToSchema } from '../../../utils/validationUtils';

const schema =
	yup.object().shape({
		certified_price: yup
			.string()
			.test(
				'minimumPrice',
				'Minimum allowed declared price is 0.20 USD',
				(value) => value ? parseFloat(value) > 0.19 : true,
			)
			.test(
				'validDollar',
				'Valid price is required, no amounts over 999.99 or fractions of cents',
				(value) => /^([1-9]\d?\d?|0)(\.\d{1,2})?$/.test(value!),
			)
			.test(
				'type',
				'Only include numbers and one period in the price, no currency signs',
				(value) => !isNaN(value as any),
			)
			
			.required('Price must be provided'),
		certified_flag: yup
			.boolean()
			.default(false)
			.required('Box must be checked')
			.test(
				'must_be_true',
				'Box must be checked',
				(value) => value === null || value === true,
			),
		user_signature: yup.string().trim().required('Signature is required'),
	});

const productTypeSchema =
	yup.object().shape({
		requested_product_type: yup
			.string()
			.required('Product Type must be selected to continue')
	});

interface RequestDeclaredPriceAgreementModalProps {
	show: boolean;
	closeModal: () => void;
	onSubmitted: () => void;
	defaultValues: Record<string, any>;
	nsUid?: string;
	productPlatformCode?: ProductData['platform_code'];
	componentGameName?: ProductComponentData['game_name'];
	readOnly?: boolean;
	requestId?: number;
	componentProductType?: ProductComponentData['product_type'];
}

const RequestDeclaredPriceAgreementModal = ({
	show,
	closeModal,

	onSubmitted,
	defaultValues = {},
	nsUid = '',
	productPlatformCode = '',
	componentGameName = '',

	readOnly = false,
	requestId,
	componentProductType,
}: RequestDeclaredPriceAgreementModalProps): ReactElement => {
	const [selectFormValues, setSelectFormValues] = useState<Record<string, string>>({});
	const [selectedProductType, setSelectedProductType] = useState<string | undefined>();
	const [formValues, setFormValues] = useState<Record<string, any>>({});
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [showAllErrors, setShowAllErrors] = useState<boolean>(false);
	const [lastUpdated, setLastUpdated] = useState<number | null >();
	const [readOnlyMode, setReadOnlyMode] = useState<boolean>(true);

	const productTypeSelectionRequired = !readOnlyMode && !componentProductType && productPlatformCode === 'HAC';
	const workingProductType = componentProductType || (productPlatformCode === 'HAC' ? selectedProductType : productPlatformCode);
	const existingDataQuery = useQuery(
		['getDeclaredPriceAgreement', requestId],
		() => getDeclaredPriceAgreement(requestId!),
		{ enabled: !!(show && requestId && readOnlyMode) },
	);

	const templateQuery = useQuery(
		['getDeclaredPriceRequestAgreementTemplates', workingProductType],
		() => getDeclaredPriceRequestAgreementTemplates(workingProductType ?? ''),
		{ enabled: !!(show && !readOnlyMode && workingProductType) },
	);
	const isLoading = existingDataQuery.isLoading || templateQuery.isLoading;
	const fault =
		!!(templateQuery.isError && templateQuery.error) ||
		!!(existingDataQuery.isError && existingDataQuery.error);
	const declaredPriceTemplate: any = readOnlyMode
		? existingDataQuery.isSuccess &&
		  existingDataQuery.data.data.declared_price_agreement_template
		: templateQuery.isSuccess && templateQuery.data.data;

	const declaredPriceTemplateBody =
		(declaredPriceTemplate?.template && declaredPriceTemplate?.template) ||
		declaredPriceTemplate?.template_body;

	useEffect(() => {
		if (show) {
			setSelectFormValues({});
			setSelectedProductType(undefined);
			setFormValues({ ...defaultValues });
			setShowAllErrors(false);
			setIsSubmitting(false);
			setLastUpdated(null);
			setReadOnlyMode(readOnly);
		}
	}, [show]);

	const changedFormValue = (values: Record<string, any>) => {
		setFormValues(values);
	};

	const productTypeValidationErrors = validateToSchema(productTypeSchema, selectFormValues);
	const validationErrors = formValues && validateToSchema(schema, formValues);

	const submitForm = async () => {
		if (isSubmitting || !workingProductType) {
			return;
		}
		if (!isEmptyObject(validationErrors)) {
			setShowAllErrors(true);
			return;
		}

		setIsSubmitting(true);

		postDeclaredPriceRequests(
			nsUid,
			{
				declared_price_agreement_template_id:
				declaredPriceTemplate.declared_price_agreement_template_id,
				certified_flag: formValues?.certified_flag,
				certified_price: formValues?.certified_price,
				user_signature: formValues?.user_signature,
				requested_product_type: selectedProductType || null,
			},
			() => setLastUpdated(new Date().valueOf()),
		)
			.then(() => {
				onSubmitted();
				closeModal();
				toasterNotify(
					`Your declared price agreement for "${componentGameName}" has been successfully submitted.`,
					'success',
				);
			})
			.catch((err) => {
				toasterNotify(
					createMessageForError(err, 'submitting your declared price agreement'),
					'error',
					err,
				);
			})
			.finally(() => {
				setLastUpdated(null);
				setIsSubmitting(false);
			});
	};


	return (
		<BaseModal
			size="xl"
			show={show}
			onCancel={closeModal}
			isSubmitting={isSubmitting}
			fault={fault}
		>
			<BaseModal.Title>
				{readOnlyMode ? 'View Declared Price Agreement' : 'Request Declared Price Agreement'}
			</BaseModal.Title>
			{!readOnlyMode && productTypeSelectionRequired && (
				<Row
					as={'section'}
					className={`justify-content-center${
						selectedProductType ? ' BaseModal__disabled-section' : ''
					}`}
				>
					<Col sm={12} lg={10}>
						<Alert variant="info">
							A Product Type must be selected to request a Declared Price Agreement
							for this component.
						</Alert>
						<Forms
							values={selectFormValues}
							onChange={(newFormValues) => setSelectFormValues(newFormValues)}
							validationErrors={productTypeValidationErrors}
							showAllErrors={showAllErrors}
						>
							<Forms.RadioList id="requested_product_type">
								<Forms.Heading>Product Type</Forms.Heading>
								<Forms.Option value="HAC">Nintendo Switch (HAC)</Forms.Option>
								<Forms.Option value="BEE">Nintendo Switch 2 (BEE)</Forms.Option>
							</Forms.RadioList>
						</Forms>
					</Col>
				</Row>
			)}
			{(readOnlyMode || workingProductType) &&
				(isLoading ? (
					<Loading />
				) : (
					<Row as={'section'} className="justify-content-center">
						<Col as={'section'} sm={12} lg={10}>
							<h4
								dangerouslySetInnerHTML={{
									__html: getSanitizedHTML(declaredPriceTemplateBody?.Title),
								}}
							/>
							<Forms
								onChange={(v) => changedFormValue(v)}
								values={
									(readOnlyMode
										? existingDataQuery.data?.data.declared_price_request
										: formValues) || {}
								}
								validationErrors={validationErrors}
								showAllErrors={showAllErrors}
							>
								<WSPRequestView
									template={declaredPriceTemplateBody}
									readOnly={readOnlyMode}
								/>

								<small
									className="float-right"
									dangerouslySetInnerHTML={{
										__html: getSanitizedHTML(
											`Legal: ${declaredPriceTemplate?.version}`,
										),
									}}
								/>
							</Forms>
						</Col>
					</Row>
				))}

			{!readOnlyMode && !(workingProductType) && (
				<BaseModal.Submit
					onClick={() => {
						if (isEmptyObject(productTypeValidationErrors)) {
							setShowAllErrors(false);
							setSelectedProductType(selectFormValues.requested_product_type);
						} else {
							setShowAllErrors(true);
						}
					}}
				>
					Select Product Type
				</BaseModal.Submit>
			)}

			{!readOnlyMode && !componentProductType && selectedProductType && (
				<BaseModal.Submit
					variant="outline-secondary"
					onClick={() => setSelectedProductType(undefined)}
				>
					Reselect Product Type
				</BaseModal.Submit>
			)}
			{!readOnlyMode && (workingProductType) && (
				<BaseModal.Submit onClick={() => submitForm()}>Submit</BaseModal.Submit>
			)}
			{!readOnlyMode && (
				<BaseModal.SubmissionStatus lastUpdated={lastUpdated!}>
					Request submission may take time to complete
				</BaseModal.SubmissionStatus>
			)}
		</BaseModal>
	);
};

export default RequestDeclaredPriceAgreementModal;
